import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { companiesAPI } from '../services/api'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'

export default function CompanyModal({ company, onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: company?.name || '',
      sort_order: company?.sort_order || '',
    }
  })

  // Set initial values when company changes (for edit mode)
  useEffect(() => {
    if (company) {
      setValue('name', company.name || '')
      setValue('sort_order', company.sort_order || '')
    }
  }, [company, setValue])

  const mutation = useMutation(
    async (data) => {
      console.log('Mutation data:', data)
      if (company) {
        console.log('Updating company with UUID:', company.uuid)
        return companiesAPI.update(company.uuid, data)
      } else {
        console.log('Creating new company')
        return companiesAPI.create(data)
      }
    },
    {
      onSuccess: (data) => {
        console.log('Mutation success:', data)
        toast.success(company ? 'Company updated successfully!' : 'Company created successfully!')
        queryClient.invalidateQueries('companies')
        onSuccess()
      },
      onError: (error) => {
        console.error('Mutation error:', error)
        toast.error(error.response?.data?.message || 'Operation failed')
      }
    }
  )

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      console.log('Form data before submission:', data)
      
      // Create data object (no FormData needed since no file upload)
      const submitData = {
        name: data.name,
        sort_order: data.sort_order ? parseInt(data.sort_order) : undefined
      }
      
      console.log('Data for submission:', submitData)
      
      mutation.mutate(submitData)
    } catch (error) {
      console.error('Company submission error:', error)
      toast.error('Failed to save company')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 overflow-y-auto h-full w-full z-50 flex items-center justify-center px-4 py-6 md:py-8">
      <div className="relative w-full max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 animate-scale-in max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {company ? `Edit Company: ${company.name}` : 'Add New Company'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
          <div className="p-5 space-y-6 overflow-y-auto flex-grow">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Company Name <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                {...register('name', { required: 'Company name is required' })}
                className="block w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter company name"
              />
              {errors.name && (
                <p className="mt-1.5 text-sm text-danger-500 font-medium">{errors.name.message}</p>
              )}
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Sort Order
              </label>
              <input
                type="number"
                {...register('sort_order', { 
                  valueAsNumber: true,
                  min: { value: 0, message: 'Sort order must be 0 or greater' }
                })}
                className="block w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter sort order (e.g., 1, 2, 3...)"
                min="0"
              />
              {errors.sort_order && (
                <p className="mt-1.5 text-sm text-danger-500 font-medium">{errors.sort_order.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Lower numbers will appear first. Leave empty to auto-assign.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 p-5 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || mutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 disabled:cursor-not-allowed rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
            >
              {isSubmitting || mutation.isLoading ? 'Saving...' : (company ? 'Update Company' : 'Create Company')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}